import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';

/** El catálogo de referencia es estático y distinto de las observaciones de campo. */
export interface ReferenceSpecies {
  id_especie: string;
  nombre_comun: string;
  nombre_cientifico: string;
  categoria: string;
  origen: string;
  caracteristicas_adaptativas: string;
  localidades_reportadas: string;
  forma_crecimiento: string;
  altura: string;
  hoja: string;
  flor: string;
  fruto: string;
  pagina_pdf: number | null;
  pagina_impresa: number | null;
  creditos_fotograficos: string;
  fuente: string;
  licencia_guia: string;
  nota_fuente: string;
  imagenes: string[];
  miniaturas: string[];
  imagen_principal: string;
  miniatura_principal: string;
}

export interface ObservedSpecies {
  key: string;
  commonName: string;
  scientificName: string;
  count: number; // registros, NO individuos
}

interface CatalogPayload {
  catalogo: { nombre: string; version: string; fuente: string; licencia_guia: string; nota_licencias: string };
  especies: ReferenceSpecies[];
}

export function normalizedTaxon(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLocaleLowerCase('es');
}

export function validCatalogPhoto(path: string): string {
  // El catálogo ya viene incluido en la app móvil. Solo se exponen sus miniaturas reales.
  return /^imagenes\/miniaturas\/[a-z0-9_-]+\.jpg$/i.test(path)
    ? `assets/catalogo/${path}` : '';
}

@Injectable({ providedIn: 'root' })
export class CatalogService {
  private readonly backend = inject(SupabaseService);
  private cached: Promise<CatalogPayload> | undefined;

  reference(): Promise<CatalogPayload> {
    if (!this.cached) {
      this.cached = fetch('assets/catalogo/catalogo_especies.json')
        .then(async response => {
          if (!response.ok) throw new Error(`No se encontró el catálogo botánico (HTTP ${response.status}).`);
          return response.json() as Promise<CatalogPayload>;
        })
        .then(payload => {
          if (!Array.isArray(payload.especies)) throw new Error('El archivo de catálogo tiene un formato incorrecto.');
          return payload;
        })
        .catch(error => { this.cached = undefined; throw error; });
    }
    return this.cached;
  }

  async get(id: string): Promise<ReferenceSpecies | null> {
    const data = await this.reference();
    return data.especies.find(species => species.id_especie === id) ?? null;
  }

  thumb(species: ReferenceSpecies, index = 0): string {
    return validCatalogPhoto(species.miniaturas[index] ?? species.miniatura_principal ?? '');
  }

  /** Consulta solo columnas de identificación, con RLS vigente y paginación. */
  async observed(): Promise<ObservedSpecies[]> {
    const grouped = new Map<string, ObservedSpecies>();
    const pageSize = 1000;
    for (let page = 0; page < 50; page++) {
      const { data, error } = await this.backend.client.from('registros_flora')
        .select('id,common_name,scientific_name')
        .order('id', { ascending: true })
        .range(page * pageSize, (page + 1) * pageSize - 1);
      if (error) throw error;
      const batch = data ?? [];
      for (const row of batch) {
        const commonName = String(row.common_name ?? '').trim();
        const scientificName = String(row.scientific_name ?? '').trim();
        if (!commonName && !scientificName) continue;
        const key = normalizedTaxon(scientificName || commonName);
        const prev = grouped.get(key);
        if (prev) prev.count++;
        else grouped.set(key, { key, commonName, scientificName, count: 1 });
      }
      if (batch.length < pageSize) {
        return [...grouped.values()].sort((a, b) =>
          (a.scientificName || a.commonName).localeCompare(b.scientificName || b.commonName, 'es'));
      }
    }
    throw new Error('Hay más de 50 000 registros. La lista de especies necesita filtrado en servidor.');
  }
}
